import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, User, Phone, ChevronLeft, ChevronRight, Check, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const PublicBookingPage = () => {
  const [step, setStep] = useState(1); // 1: Date, 2: Time, 3: Info, 4: Confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [patientInfo, setPatientInfo] = useState({
    full_name: '',
    phone: '',
    motivo_consulta: ''
  });

  // Fetch blocked dates on mount and month change
  const fetchBlockedDates = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 2, 0).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/api/availability/blocked-dates?start=${startDate}&end=${endDate}`);
      setBlockedDates(response.data.blocked_dates || []);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);

  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.includes(dateStr);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const isPast = date < today;
      const isWeekend = date.getDay() === 0; // Sunday
      const isBlocked = isDateBlocked(date);
      days.push({
        day,
        date,
        disabled: isPast || isWeekend || isBlocked,
        isToday: date.toDateString() === today.toDateString(),
        isBlocked: isBlocked
      });
    }
    
    return days;
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/api/availability/slots/${dateStr}`);
      // Extract available times from slots array
      const slots = response.data.slots || [];
      const availableTimes = slots.filter(s => s.available).map(s => s.time);
      setAvailableSlots(availableTimes);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
      toast.error('Error al cargar horarios disponibles');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (dateInfo) => {
    if (dateInfo.disabled) return;
    setSelectedDate(dateInfo.date);
    setSelectedTime(null);
    fetchAvailableSlots(dateInfo.date);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!patientInfo.full_name.trim() || !patientInfo.phone.trim()) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    
    setSubmitting(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      await axios.post(`${API}/api/public/book`, {
        full_name: patientInfo.full_name,
        phone: patientInfo.phone,
        appointment_date: dateStr,
        appointment_time: selectedTime,
        appointment_type: 'consulta',
        motivo_consulta: patientInfo.motivo_consulta
      });
      
      setBookingComplete(true);
      setStep(4);
      toast.success('Cita agendada exitosamente');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al agendar cita';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setPatientInfo({ full_name: '', phone: '', motivo_consulta: '' });
    setBookingComplete(false);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('es-GT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const days = generateCalendarDays();
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header with Logo */}
      <div className="bg-slate-900 shadow-lg border-b border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex justify-center">
            <img 
              src="/images/logo-acevedo.png" 
              alt="Dr. Cristian R. Acevedo - Médico Oftalmólogo" 
              className="h-52 object-contain"
            />
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Progress indicator */}
        {!bookingComplete && (
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s ? 'bg-cyan-500 text-white' : 'bg-slate-600 text-slate-400'
                  }`}>
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-8 h-0.5 ${step > s ? 'bg-cyan-500' : 'bg-slate-600'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Select Date */}
        {step === 1 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Selecciona una fecha</h2>
              </div>
              
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-medium text-white">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => (
                  <div key={i} className="text-xs font-medium text-slate-400 py-2">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((dayInfo, index) => (
                  <button
                    key={index}
                    onClick={() => dayInfo && !dayInfo.disabled && handleDateSelect(dayInfo)}
                    disabled={!dayInfo || dayInfo.disabled}
                    className={`
                      aspect-square rounded-lg text-sm font-medium transition-all
                      ${!dayInfo ? 'invisible' : ''}
                      ${dayInfo?.disabled && !dayInfo?.isBlocked ? 'text-slate-600 cursor-not-allowed' : ''}
                      ${dayInfo?.isBlocked ? 'bg-red-900/30 text-red-400 cursor-not-allowed line-through' : ''}
                      ${!dayInfo?.disabled && !dayInfo?.isBlocked ? 'text-slate-200 hover:bg-cyan-600/30 cursor-pointer' : ''}
                      ${dayInfo?.isToday ? 'ring-2 ring-cyan-500 ring-offset-1 ring-offset-slate-800' : ''}
                      ${selectedDate?.toDateString() === dayInfo?.date?.toDateString() ? 'bg-cyan-500 text-white' : ''}
                    `}
                  >
                    {dayInfo?.day}
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-slate-400 mt-4 text-center">
                Los domingos y días marcados en rojo no hay atención
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <button 
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                Volver
              </button>
              
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Selecciona la hora</h2>
              </div>
              <p className="text-sm text-slate-400 mb-4 capitalize">{formatDate(selectedDate)}</p>
              
              {loadingSlots ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-400">No hay horarios disponibles para esta fecha</p>
                  <Button onClick={goBack} variant="outline" className="mt-4 border-slate-600 text-slate-300 hover:bg-slate-700">
                    Seleccionar otra fecha
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="public-slots-grid">
                  {availableSlots.map((time) => (
                    <button
                      key={time}
                      onClick={() => handleTimeSelect(time)}
                      className={`
                        py-3 px-2 rounded-lg text-sm font-medium transition-all border
                        ${selectedTime === time 
                          ? 'bg-cyan-500 text-white border-cyan-500' 
                          : 'bg-slate-700 text-slate-200 border-slate-600 hover:border-cyan-400 hover:bg-slate-600'
                        }
                      `}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Patient Info */}
        {step === 3 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <button 
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                Volver
              </button>
              
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Tus datos</h2>
              </div>
              
              {/* Summary */}
              <div className="bg-cyan-900/30 border border-cyan-700/50 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-cyan-400">Fecha:</span> <span className="capitalize">{formatDate(selectedDate)}</span>
                </p>
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-cyan-400">Hora:</span> {selectedTime}
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">
                    Nombre completo *
                  </label>
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={patientInfo.full_name}
                    onChange={(e) => setPatientInfo({ ...patientInfo, full_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">
                    Teléfono *
                  </label>
                  <Input
                    type="tel"
                    placeholder="+502 1234-5678"
                    value={patientInfo.phone}
                    onChange={(e) => setPatientInfo({ ...patientInfo, phone: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Motivo de consulta
                  </label>
                  <Textarea
                    placeholder="Describe brevemente el motivo de tu consulta..."
                    value={patientInfo.motivo_consulta}
                    onChange={(e) => setPatientInfo({ ...patientInfo, motivo_consulta: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    rows={3}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                  disabled={submitting}
                >
                  {submitting ? 'Agendando...' : 'Confirmar Cita'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && bookingComplete && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              
              <h2 className="text-xl font-bold text-white mb-2">¡Cita Agendada!</h2>
              <p className="text-slate-400 mb-4">Tu cita ha sido confirmada</p>
              
              <div className="bg-slate-700 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-300 mb-1">
                  <span className="font-medium text-cyan-400">Paciente:</span> {patientInfo.full_name}
                </p>
                <p className="text-sm text-slate-300 mb-1">
                  <span className="font-medium text-cyan-400">Fecha:</span> <span className="capitalize">{formatDate(selectedDate)}</span>
                </p>
                <p className="text-sm text-slate-300 mb-1">
                  <span className="font-medium text-cyan-400">Hora:</span> {selectedTime}
                </p>
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-cyan-400">Teléfono:</span> {patientInfo.phone}
                </p>
                {patientInfo.motivo_consulta && (
                  <p className="text-sm text-slate-300 mt-2 pt-2 border-t border-slate-600">
                    <span className="font-medium text-cyan-400">Motivo:</span> {patientInfo.motivo_consulta}
                  </p>
                )}
              </div>
              
              <p className="text-xs text-slate-500 mb-4">
                Te contactaremos si hay algún cambio
              </p>
              
              <Button onClick={resetBooking} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                Agendar otra cita
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 py-3">
        <p className="text-center text-xs text-slate-500">
          Dr. Cristian R. Acevedo · Médico Oftalmólogo
        </p>
      </div>
    </div>
  );
};

export default PublicBookingPage;
